import { useState, useCallback, ReactNode, useRef } from "react";
import { createPortal } from "react-dom";
import { Dialog } from "primereact/dialog";
import { DataTable, DataTableExpandedRows, DataTableValueArray } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { useTableHeader, SearchField } from "./useTableHeader";

const getValue = (obj: any, path: string) =>
  path.split(".").reduce((acc, key) => acc?.[key], obj);

export interface SelectDialogColumn<T> {
  field: string;
  header: string;
  render?: (item: T) => any;
}

export interface SelectOpenOptions<T> {
  title: string;
  data: T[];
  columns: SelectDialogColumn<T>[];
  loading?: boolean;
  onFilter?: (params: { filter?: any; pageIndex?: number; pageSize?: number }) => Promise<{ data: T[]; total: number }>;
  searchFields?: SearchField[];
  rowExpansionTemplate?: (item: T) => ReactNode;
  multiple?: boolean;
  initialSelection?: T[];
}

export function useSelectDialog<T extends { id: number | string }>() {
  const [state, setState] = useState({
    isOpen: false,
    title: "",
    data: [] as T[],
    columns: [] as SelectDialogColumn<T>[],
    loading: false,
    onFilter: undefined as any,
    searchFields: [] as SearchField[],
    rowExpansionTemplate: undefined as any,
    first: 0,
    rows: 10,
    total: 0,
    multiple: false,
  }); 
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | DataTableValueArray | undefined>(undefined);
  const [selection, setSelection] = useState<T[]>([]);
  const resolverRef = useRef<((item: any) => void) | null>(null);

  const internalFetch = useCallback(async (onFilter: any, filter: any, pageIndex: number, pageSize: number) => {
    if (!onFilter) return;
    
    setState(s => ({ ...s, loading: true }));
    
    try {
        const result = await onFilter({ 
            filter: filter ?? { logic: "and", filters: [] }, 
            pageIndex, 
            pageSize 
        });

        if (result) {
            // Axios response'u ya da direkt data objesini güvenli yakalayabilmek için
            const items = result.data?.items || result.items || result.data || (Array.isArray(result) ? result : []);
            const totalCount = result.data?.total || result.total || items.length;

            setState(s => ({ 
                ...s, 
                data: items, 
                total: totalCount, 
                loading: false,
            }));
        }
        console.log("useSelectDialog", state)
    } catch (error) {
        setState(s => ({ ...s, loading: false }));
    }
  }, []);

  // --- OVERLOADS ---
  function open(options: SelectOpenOptions<T> & { multiple: true }): Promise<T[] | null>;
  function open(options: SelectOpenOptions<T> & { multiple?: false }): Promise<T | null>;
  function open(options: SelectOpenOptions<T>): Promise<T | T[] | null> {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setSelection(options.initialSelection || []);
      
      setState({
            isOpen: true,
            title: options.title,
            data: options.data || [], 
            columns: options.columns,
            loading: !!options.onFilter,
            onFilter: options.onFilter,
            searchFields: options.searchFields ?? [{ field: "name", operator: "contains" }],
            rowExpansionTemplate: options.rowExpansionTemplate,
            first: 0, // 🌟 Her açılışta sıfırlandığından emin oluyoruz
            rows: 10,
            total: options.data?.length || 0,
            multiple: options.multiple ?? false,
        });
        
        if (options.onFilter) {
            internalFetch(options.onFilter, null, 1, 10);
        }
    });
  } 

  const { header, dynamicFilter, setGlobalFilterValue } = useTableHeader({ 
    title: state.title,
    searchFields: state.searchFields,
    onFilterChange: (filter) => {
        // 🌟 Arama filtresi değiştiğinde de sayfayı 1'e çekmek (first: 0) UX açısından kaymaları önler
        if (state.isOpen) {
          setState(s => ({ ...s, first: 0 }));
          internalFetch(state.onFilter, filter, 1, state.rows);
        }
    },
  });

  const close = useCallback(() => {
    setState((s) => ({ ...s, isOpen: false }));
    setGlobalFilterValue(""); 
    if (resolverRef.current) {
        resolverRef.current(null);
        resolverRef.current = null;
    }
  }, [setGlobalFilterValue]); 

  const selectItem = useCallback((item: any) => {
    setState((s) => ({ ...s, isOpen: false }));
    setGlobalFilterValue(""); 
    if (resolverRef.current) {
        resolverRef.current(item);
        resolverRef.current = null;
    }
  }, [setGlobalFilterValue]);

  const confirmMultiple = useCallback(() => {
    setState((s) => ({ ...s, isOpen: false }));
    if (resolverRef.current) {
      resolverRef.current(selection);
      resolverRef.current = null;
    }
  }, [selection]);

  const modal = state.isOpen
    ? createPortal(
        <Dialog 
          key={state.isOpen ? "opened" : "closed"}
          visible={state.isOpen} 
          onHide={close} 
          header={header} 
          style={{ width: "60rem", height: "85%", zIndex: 9999 }} 
          maskStyle={{ zIndex: 9999 }}
          modal 
          maximizable
          resizable={false}
          footer={state.multiple && (
            <div className="flex justify-end gap-2">
                <Button label="İptal" icon="pi pi-times" className="p-button-text" onClick={close} />
                <Button label="Seçilenleri Ekle" icon="pi pi-check" onClick={confirmMultiple} />
            </div>
          )}
        >
          <DataTable
            value={state.data as any}
            loading={state.loading}
            lazy
            paginator
            first={state.first}
            rows={state.rows}
            totalRecords={state.total}
            dataKey="id"
            expandedRows={expandedRows}
            emptyMessage="Kayıt bulunamadı."
            onRowToggle={(e) => setExpandedRows(e.data)}
            rowExpansionTemplate={state.rowExpansionTemplate}
            selectionMode={state.multiple ? 'multiple' : (undefined as any)}
            selection={state.multiple ? selection : null}
            onSelectionChange={(e: any) => setSelection(e.value)}
            onPage={(event) => {
              const newFirst = event.first ?? 0;
              const newRows = event.rows ?? 10;
              const pageIndex = Math.floor(newFirst / newRows) + 1;

              setState(s => ({ ...s, first: newFirst, rows: newRows }));
              internalFetch(state.onFilter, dynamicFilter, pageIndex, newRows);
            }}
          >
            {state.multiple && <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />}
            {state.rowExpansionTemplate && <Column expander style={{ width: "3rem" }} />}
            {state.columns.map((col) => (
              <Column key={col.field} header={col.header} body={(row: T) => col.render ? col.render(row) : getValue(row, col.field)} />
            ))}
            {!state.multiple && (
              <Column header="" style={{ width: 90 }} body={(row: T) => (
                <Button label="Seç" icon="pi pi-check" className="p-button-sm" onClick={() => selectItem(row)} />
              )} />
            )}
          </DataTable>
        </Dialog>,
        document.body
      )
    : null;

  return { open, modal, dynamicFilter, selectItem };
}